import React, { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";

/**
 * Draggable Before/After comparison slider.
 * Props: beforeUrl, afterUrl, caption
 */
export default function BeforeAfterSlider({ beforeUrl, afterUrl, caption, beforeLabel, afterLabel, testid }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // percent
  const draggingRef = useRef(false);

  const clientXFromEvent = (e) => {
    if (e.touches && e.touches[0]) return e.touches[0].clientX;
    return e.clientX;
  };

  const updatePos = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  }, []);

  const onMove = useCallback((e) => {
    if (!draggingRef.current) return;
    updatePos(clientXFromEvent(e));
  }, [updatePos]);

  const stopDrag = useCallback(() => {
    draggingRef.current = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", stopDrag);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", stopDrag);
  }, [onMove]);

  const startDrag = useCallback((e) => {
    draggingRef.current = true;
    updatePos(clientXFromEvent(e));
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", stopDrag);
  }, [onMove, stopDrag, updatePos]);

  return (
    <figure className="w-full" data-testid={testid || "before-after-slider"}>
      <div
        ref={containerRef}
        className="relative w-full aspect-[16/10] overflow-hidden bg-[color:var(--gcs-emerald)] select-none cursor-ew-resize rounded-sm"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        {/* Before (bottom layer) */}
        <img
          src={beforeUrl}
          alt="Antes"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          draggable={false}
        />
        <span className="absolute top-4 left-4 z-30 overline text-[color:var(--gcs-gold)] bg-[color:var(--gcs-emerald)]/70 backdrop-blur px-3 py-1 rounded-full">
          {beforeLabel || "Antes"}
        </span>

        {/* After (clipped to slider position) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        >
          <img
            src={afterUrl}
            alt="Después"
            className="w-full h-full object-cover pointer-events-none"
            draggable={false}
          />
          <span className="absolute top-4 right-4 z-30 overline text-[color:var(--gcs-emerald)] bg-[color:var(--gcs-gold)] px-3 py-1 rounded-full">
            {afterLabel || "Después"}
          </span>
        </div>

        {/* Handle line */}
        <div
          className="absolute top-0 bottom-0 z-40 pointer-events-none"
          style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
        >
          <div className="w-[2px] h-full bg-[color:var(--gcs-ivory)]" />
          <motion.button
            type="button"
            aria-label="Arrastra para comparar"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto rounded-full h-12 w-12 md:h-14 md:w-14 bg-[color:var(--gcs-ivory)] border border-[color:var(--gcs-gold)] shadow-[0_10px_30px_rgba(0,0,0,0.35)] flex items-center justify-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onMouseDown={(e) => { e.stopPropagation(); startDrag(e); }}
            onTouchStart={(e) => { e.stopPropagation(); startDrag(e); }}
            data-testid={`${testid || "ba-slider"}-handle`}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M8 6L4 12L8 18" stroke="var(--gcs-emerald)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16 6L20 12L16 18" stroke="var(--gcs-emerald)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.button>
        </div>
      </div>
      {caption && (
        <figcaption className="mt-3 flex items-center gap-2 text-[color:var(--gcs-emerald)]/70">
          <span className="h-px w-6 bg-[color:var(--gcs-gold)]" />
          <span className="overline text-[color:var(--gcs-gold)]">{caption}</span>
        </figcaption>
      )}
    </figure>
  );
}
